import * as React from 'react';
import { useContext, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { CircularProgress, TextField, Tooltip, Typography } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useTranslation } from 'react-i18next';
import * as Styled from './styled';
import { RootStoreContext } from '../../App';
import { AiNarrativeResponse } from 'api/types/types';
import ImmutableTimeEntry from 'api/immutables/ImmutableTimeEntry';
import { theme } from 'common/theme';

interface Props {
    timeEntry: ImmutableTimeEntry;
    narrativeText: string | null | undefined;
    setWarning: (message: string) => void;
    onAcceptSuggestion: (suggestion: string) => void;
    updateAiUsageCount: () => void;
    updateNoChangeRequiredCount: () => void;
    aiUsageCount?: number;
    isGridView?: boolean;
    aiOnPost?: boolean;
    aiResponse?: AiNarrativeResponse | null | string;
    setAiResponse?: (res: AiNarrativeResponse | null | string) => void;
    error: boolean;
}

const AiComponent = (props: Props) => {
    const [disableAiWand, setDisableAiWand] = useState<boolean>(false);
    const [suggestion, setSuggestion] = useState<string>('');
    const [changes, setChanges] = useState<string>('');
    const [noChangeFlag, setNoChangeFlag] = useState<boolean>(false);
    const [invalidAiSuggestion, setInvalidAiSuggestion] = useState<boolean>(false);
    const [displayAiSuggestion, setDisplayAiSuggestion] = useState<boolean>(false);
    const [rejectedAiNarrative, setRejectedAiNarrative] = useState<AiNarrativeResponse | null>(null);
    const { t } = useTranslation(['narratives', 'timeentries']);
    const rootStore = useContext(RootStoreContext);
    const isMounted = useRef(false);
    const suggestionRef = useRef<HTMLElement>(null);
    const prevMatterId = useRef<number | null>();

    const { rewriteNarrative, setAiOnPostEnabled, aiLoading, setAiLoading } = rootStore.narrativeStore;
    const {
        timeEntry,
        narrativeText,
        setWarning,
        onAcceptSuggestion,
        updateAiUsageCount,
        updateNoChangeRequiredCount,
        isGridView,
        aiOnPost,
        aiResponse,
        aiUsageCount,
        setAiResponse,
        error
    } = props;

    useEffect(() => {
        if (aiResponse) {
            if (typeof aiResponse === 'string') {
                setWarning(aiResponse);
            } else {
                displaySuggestion(aiResponse);
            }
        }
    }, []);

    useEffect(() => {
        if (isMounted.current) {
            setRejectedAiNarrative(null);
            setNoChangeFlag(false);
            setWarning('');
            setDisableAiWand(false);
            if (setAiResponse) {
                setAiResponse(null);
            }
            if (prevMatterId.current !== timeEntry.matterId) {
                setDisplayAiSuggestion(false);
                prevMatterId.current = timeEntry.matterId;
            }
        } else {
            isMounted.current = true;
        }
    }, [narrativeText, timeEntry.matterId]);

    useEffect(() => {
        if (aiOnPost) {
            setWarning('');
            if (timeEntry.matterId) {
                getAiSuggestion(true);
            }
            setAiOnPostEnabled(false);
        }
    }, [aiOnPost]);

    const _setWarning = (message: string) => {
        setWarning(message);
        if (setAiResponse) {
            setAiResponse(message);
        }
    }

    const getAiSuggestion = async (fromPost: boolean = false) => {
        setAiLoading(true);
        setDisableAiWand(true);
        setDisplayAiSuggestion(false);
        setInvalidAiSuggestion(false);
        if ((aiUsageCount || 0) >= rootStore.appStore.maxLimitAiNarrativeRewrite) {
            _setWarning(t('narrative_field.ai.icon.generate.warning.limit'));
            setAiLoading(false);
            return;
        }
        const response = rejectedAiNarrative ? rejectedAiNarrative : await rewriteNarrative(narrativeText!, timeEntry!, t);
        if (!response) {
            if (response === null && !fromPost) {
                _setWarning(t('narrative_field.ai.icon.generate.warning.length'));
            }
            setDisableAiWand(false);
            setAiLoading(false);
            return;
        }
        if (!!response.invalidMessage) {
            setInvalidAiSuggestion(true);
            setWarning(t(response.invalidMessage));
            setAiLoading(false);
            return;
        }
        // only update AI usage count if API has been executed and not loaded from previous suggestion
        if (!rejectedAiNarrative) {
            if (narrativeText !== response.rewrittenNarrative) {
                updateAiUsageCount();
            } else {
                updateNoChangeRequiredCount();
            }
        }
        if (setAiResponse) {
            setAiResponse(response);
        }
        displaySuggestion(response);
        setAiLoading(false);
        if (suggestionRef.current) {
            suggestionRef.current.focus();
        }
    }

    const displaySuggestion = (res: AiNarrativeResponse) => {
        setDisableAiWand(true);
        setSuggestion(res.rewrittenNarrative);
        setChanges(res.tooltip);
        if (narrativeText !== res.rewrittenNarrative) {
            setDisplayAiSuggestion(true);
        } else {
            setNoChangeFlag(true);
        }
    }

    const handleAccept = async () => {
        await onAcceptSuggestion(suggestion);
        setDisplayAiSuggestion(false);
        setDisableAiWand(true);
    }

    const handleReject = () => {
        setRejectedAiNarrative({
            rewrittenNarrative: suggestion,
            tooltip: changes
        });
        setDisplayAiSuggestion(false);
        setDisableAiWand(false);
        if (setAiResponse) {
            setAiResponse(null);
        }
    }

    return (
        <>
            {aiLoading ?
                <Styled.WandIconContainer><CircularProgress size={16} color="primary" /></Styled.WandIconContainer>
            :
                <Tooltip title={t('narrative_field.ai.icon.generate.tooltip')} placement="top">
                    <Styled.WandIconContainer
                        disabled={!narrativeText || !timeEntry.matterId || disableAiWand}
                        onClick={() => getAiSuggestion()}
                    >
                        <FontAwesomeIcon icon={faWandMagicSparkles as IconProp} />
                    </Styled.WandIconContainer>
                </Tooltip>
            }
            {invalidAiSuggestion &&
                <Styled.AiSuggestionContainer>
                    <Styled.WarningMessage>{t('narrative_field.ai.icon.generate.warning.invalid_suggestion')}</Styled.WarningMessage>
                </Styled.AiSuggestionContainer>
            }
            {displayAiSuggestion &&
                <Styled.AiSuggestionContainer>
                    <TextField
                        inputRef={suggestionRef}
                        value={suggestion}
                        label={t('narrative_field.ai.suggestion.text.label')}
                        style={{ padding: '5px', fontSize: '14px !important' }}
                        fullWidth={true}
                        onChange={() => setDisplayAiSuggestion(true)}
                        InputProps={{
                            readOnly: true,
                            disableUnderline: true,
                            inputProps: {
                                'aria-label': t('narrative_field.ai.suggestion.text.label')
                            },
                            endAdornment: (
                                <Styled.HtmlTooltip
                                    title={
                                        <>
                                            <Typography>{t('narrative_field.ai.suggestion.tooltip')}</Typography>
                                            <Styled.ScrollableDiv>{changes}</Styled.ScrollableDiv>
                                        </>
                                    }
                                    interactive={true}
                                    placement="right"
                                >
                                    <Styled.HelpIcon fontSize="small" tabIndex={0}/>
                                </Styled.HtmlTooltip>
                            )
                        }}
                        InputLabelProps={{
                            style: { padding: '5px', color: theme.highlighted.aiLabel }
                        }}
                        rowsMax={3}
                        multiline={true}
                    />
                    <Styled.StyledButton onClick={handleAccept}>
                        {t('narrative_field.ai.suggestion.button.accept')}
                    </Styled.StyledButton>
                    <Styled.StyledButton onClick={handleReject}>
                        {t('narrative_field.ai.suggestion.button.reject')}
                    </Styled.StyledButton>
                </Styled.AiSuggestionContainer>
            }
            {noChangeFlag && !error &&
                <Styled.InfoContainer isGridView={!!isGridView}>
                    {t('narrative_field.ai.info.no_change')}
                </Styled.InfoContainer>
            }
        </>
    );
}

export default observer(AiComponent);
